import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import DiagramEditor from './DiagramEditor';
import QuestionDiagram from './QuestionDiagram';
import {
    buildQuestionDiagramPayload,
    createEmptyExcalidrawScene,
    DIAGRAM_FORMATS,
    normalizeQuestionDiagram,
} from '../Shared/questionDiagramUtils';

const parseQuestionContent = (data) => {
    if (!data?.choices?.[0]?.message?.content) {
        return null;
    }

    try {
        return JSON.parse(data.choices[0].message.content);
    } catch (error) {
        console.error('Failed to parse question content', error);
        return null;
    }
};

function QuestionAnsComp({ data, onChange, questionTitle1, isVerifierMode }) {
    const [questionTitle, setQuestionTitle] = useState('');
    const [question, setQuestion] = useState('');
    const [options, setOptions] = useState({});
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [steps, setSteps] = useState('');
    const [diagramFormat, setDiagramFormat] = useState(DIAGRAM_FORMATS.NONE);
    const [diagramCode, setDiagramCode] = useState('');
    const [diagramScene, setDiagramScene] = useState(null);
    const [isDiagramModalOpen, setDiagramModalOpen] = useState(false);

    const resolveCorrectAnswerValue = useCallback((rawCorrectAnswer, rawOptions) => {
        const normalizedAnswer = String(rawCorrectAnswer == null ? '' : rawCorrectAnswer).trim();
        if (!normalizedAnswer) {
            return '';
        }
        if (rawOptions && Object.prototype.hasOwnProperty.call(rawOptions, normalizedAnswer)) {
            return rawOptions[normalizedAnswer];
        }
        return normalizedAnswer;
    }, []);

    const currentDiagram = useMemo(
        () => normalizeQuestionDiagram({ diagramFormat, diagramCode, diagramScene }),
        [diagramCode, diagramFormat, diagramScene]
    );

    const emitChange = useCallback((nextValues = {}) => {
        const nextDiagramPayload = buildQuestionDiagramPayload({
            diagramFormat: nextValues.diagramFormat ?? diagramFormat,
            diagramCode: nextValues.diagramCode ?? diagramCode,
            diagramScene: nextValues.diagramScene ?? diagramScene,
        });

        onChange({
            questionTitle: nextValues.questionTitle ?? questionTitle,
            question: nextValues.question ?? question,
            options: nextValues.options ?? options,
            correctAnswer: nextValues.correctAnswer ?? correctAnswer,
            steps: nextValues.steps ?? steps,
            ...nextDiagramPayload,
        });
    }, [correctAnswer, diagramCode, diagramFormat, diagramScene, onChange, options, question, questionTitle, steps]);

    useEffect(() => {
        const parsedData = parseQuestionContent(data);
        if (!parsedData) {
            return;
        }

        const normalizedDiagram = normalizeQuestionDiagram(parsedData);
        setQuestion(parsedData.question || '');
        setOptions(parsedData.options || {});
        setCorrectAnswer(resolveCorrectAnswerValue(parsedData.correct_answer, parsedData.options));
        setSteps(Array.isArray(parsedData.steps) ? parsedData.steps.join('\n') : (parsedData.steps || ''));
        setDiagramFormat(normalizedDiagram.diagramFormat);
        setDiagramCode(normalizedDiagram.diagramCode || '');
        setDiagramScene(normalizedDiagram.diagramScene);
    }, [data, resolveCorrectAnswerValue]);

    useEffect(() => {
        if (questionTitle1) {
            setQuestionTitle(questionTitle1);
        }
    }, [questionTitle1]);

    const toggleDiagramModal = () => {
        setDiagramModalOpen((previousValue) => !previousValue);
    };

    const handleQuestionTitleChange = (event) => {
        const updatedTitle = event.target.value;
        setQuestionTitle(updatedTitle);
        emitChange({ questionTitle: updatedTitle });
    };

    const handleQuestionChange = (event) => {
        const updatedQuestion = event.target.value;
        setQuestion(updatedQuestion);
        emitChange({ question: updatedQuestion });
    };

    const handleOptionChange = (key, value) => {
        const updatedOptions = { ...options, [key]: value };
        setOptions(updatedOptions);
        emitChange({ options: updatedOptions });
    };

    const handleCorrectAnswerChange = (event) => {
        const updatedCorrectAnswer = event.target.value;
        setCorrectAnswer(updatedCorrectAnswer);
        emitChange({ correctAnswer: updatedCorrectAnswer });
    };

    const handleStepsChange = (event) => {
        const updatedSteps = event.target.value;
        setSteps(updatedSteps);
        emitChange({ steps: updatedSteps });
    };

    const handleCreateDiagram = () => {
        const emptyScene = createEmptyExcalidrawScene();
        setDiagramFormat(DIAGRAM_FORMATS.EXCALIDRAW);
        setDiagramCode('');
        setDiagramScene(emptyScene);
        setDiagramModalOpen(true);
        emitChange({
            diagramFormat: DIAGRAM_FORMATS.EXCALIDRAW,
            diagramCode: '',
            diagramScene: emptyScene,
        });
    };

    const handleSaveDiagramScene = (updatedScene) => {
        const normalizedScene = normalizeQuestionDiagram({
            diagramFormat: DIAGRAM_FORMATS.EXCALIDRAW,
            diagramScene: updatedScene,
        });

        setDiagramFormat(normalizedScene.diagramFormat);
        setDiagramCode('');
        setDiagramScene(normalizedScene.diagramScene);
        emitChange({
            diagramFormat: normalizedScene.diagramFormat,
            diagramCode: '',
            diagramScene: normalizedScene.diagramScene,
        });
        toggleDiagramModal();
    };

    const showLegacyDiagramNotice = currentDiagram.isSvg;
    const canEditExcalidrawDiagram = currentDiagram.isExcalidraw;
    const hasAnyDiagram = currentDiagram.hasDiagram;

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <label>Diagram:</label>
                {hasAnyDiagram ? (
                    <div style={{ marginTop: 8 }}>
                        <QuestionDiagram
                            diagram={currentDiagram}
                            minExcalidrawHeight={320}
                            maxExcalidrawHeight={720}
                            showLegacyNotice={showLegacyDiagramNotice}
                        />
                    </div>
                ) : (
                    <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                        No diagram attached to this question.
                    </div>
                )}

                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!hasAnyDiagram && !isVerifierMode && (
                        <Button color="secondary" type="button" onClick={handleCreateDiagram}>
                            Create Diagram
                        </Button>
                    )}
                    {canEditExcalidrawDiagram && !isVerifierMode && (
                        <Button color="secondary" type="button" onClick={toggleDiagramModal}>
                            Edit Diagram
                        </Button>
                    )}
                </div>

                {showLegacyDiagramNotice && (
                    <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                        Legacy SVG diagrams remain supported for display, but they are read-only in the new authoring flow.
                    </div>
                )}
            </div>

            {isDiagramModalOpen && (
                <div
                    style={{
                        marginBottom: 16,
                        border: '1px solid #d1d5db',
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: 'var(--bg-page)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            borderBottom: '1px solid #e5e7eb',
                            background: 'var(--bg-subtle)',
                        }}
                    >
                        <strong>Edit Diagram</strong>
                        <Button color="secondary" size="sm" onClick={toggleDiagramModal}>
                            Close
                        </Button>
                    </div>
                    <div style={{ height: '70vh', minHeight: 480 }}>
                        <DiagramEditor
                            scene={diagramScene || createEmptyExcalidrawScene()}
                            onSceneChange={handleSaveDiagramScene}
                        />
                    </div>
                </div>
            )}

            <div>
                <label>Question Title:</label>
                <input
                    type="text"
                    value={questionTitle}
                    onChange={handleQuestionTitleChange}
                    className="form-control"
                />
            </div>

            <div>
                <label>Question:</label>
                <textarea
                    value={question}
                    onChange={handleQuestionChange}
                    className="form-control"
                    style={{ width: '100%', height: '80px', fontFamily: 'Courier New, monospace', whiteSpace: 'pre' }}
                />
            </div>

            <div>
                <label>Options:</label>
                {Object.keys(options).map((key, index) => (
                    <div key={index}>
                        <label>{key}:</label>
                        <input
                            type="text"
                            value={options[key]}
                            onChange={(event) => handleOptionChange(key, event.target.value)}
                            className="form-control"
                            style={{ width: '100%' }}
                        />
                    </div>
                ))}
            </div>

            <div>
                <label>Correct Answer:</label>
                <input
                    type="text"
                    value={correctAnswer}
                    onChange={handleCorrectAnswerChange}
                    className="form-control"
                    style={{ width: '100%' }}
                />
            </div>

            <div>
                <label>Steps:</label>
                <textarea
                    value={steps}
                    onChange={handleStepsChange}
                    className="form-control"
                    style={{ width: '100%', height: '120px', fontFamily: 'Courier New, monospace', whiteSpace: 'pre' }}
                />
            </div>
        </div>
    );
}

export default QuestionAnsComp;
